import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Sprout, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", password: "", company_name: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      await register(form);
      toast.success("Workspace berhasil dibuat!");
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 text-white" style={{ background: "linear-gradient(135deg,#022C22 0%,#064E3B 60%,#047857 100%)" }}>
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center">
            <Sprout className="w-5 h-5 text-emerald-200" />
          </div>
          <div className="font-bold text-xl" style={{ fontFamily: "Outfit" }}>SatuCRM</div>
        </div>
        <div>
          <h1 className="text-4xl font-bold leading-tight mb-4" style={{ fontFamily: "Outfit" }}>
            From Leads to <br /> Loyal Customers.
          </h1>
          <p className="text-emerald-100/80 max-w-md">Dibuat khusus untuk UMKM, tim sales, freelancer, dan agensi Indonesia.</p>
        </div>
        <div className="text-xs text-emerald-200/60">© 2026 SatuCRM</div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{t("auth.registerTitle")}</h2>
          <p className="text-sm text-slate-500 mt-2">{t("auth.registerSub")}</p>

          <form onSubmit={submit} className="space-y-4 mt-6">
            <div className="space-y-1.5">
              <Label>{t("auth.fullName")}</Label>
              <Input required data-testid="reg-name" value={form.full_name}
                onChange={(e)=>setForm({...form,full_name:e.target.value})} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("auth.company")}</Label>
              <Input required data-testid="reg-company" value={form.company_name}
                onChange={(e)=>setForm({...form,company_name:e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("auth.email")}</Label>
                <Input type="email" required data-testid="reg-email" value={form.email}
                  onChange={(e)=>setForm({...form,email:e.target.value})} />
              </div>
              <div className="space-y-1.5">
                <Label>{t("auth.phone")}</Label>
                <Input data-testid="reg-phone" value={form.phone}
                  onChange={(e)=>setForm({...form,phone:e.target.value})} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("auth.password")}</Label>
              <Input type="password" required minLength={6} data-testid="reg-password" value={form.password}
                onChange={(e)=>setForm({...form,password:e.target.value})} />
            </div>
            {err && <div className="text-sm text-rose-600" data-testid="reg-error">{err}</div>}
            <Button type="submit" disabled={loading} className="brand-btn w-full h-11" data-testid="reg-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : t("auth.register")}
            </Button>
          </form>
          <p className="text-sm text-slate-500 mt-6 text-center">
            {t("auth.haveAccount")} <Link to="/login" className="text-emerald-800 font-medium hover:underline" data-testid="go-login">{t("auth.login")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
